import { Card, BlockStack, TextField, Text } from '@shopify/polaris';
import EmailEditor, { EditorRef } from 'react-email-editor';
import { StyledTemplateEditor } from './styled';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLazyGetTemplateDetailQuery, useUpdateTemplateMutation } from '@/redux/api/api.caller';
import { useDispatch, useSelector } from 'react-redux';
import { emailTemplatesSelector, emailTemplatesTabSelector, orderStatusSelector } from '@/redux/features/email.slice';
import emailSlice from '@/redux/features/email.slice';
import commonSlice from '@/redux/features/common.slice';
import { getErrorFromAPI, handleToastMutation } from '@/helpers/toast';
import { ErrorBanner, ControlBar } from '@/components';
import TemplateButtons from '../TemplateButtons';

// Derive the design type from the editor's public API (loadDesign param) so it
// stays in sync with react-email-editor without importing internal unlayer paths.
type JSONTemplate = Parameters<NonNullable<EditorRef['editor']>['loadDesign']>[0];

// Content dirty-detection is CONTENT-BASED: each `design:updated` event exports
// the current design and compares it against a baseline snapshot of the loaded
// design. The baseline is seeded DETERMINISTICALLY from `design:loaded` (fired
// once unlayer finishes applying a programmatic loadDesign), NOT from the
// `design:updated` stream — because unlayer does not emit `design:updated` for a
// programmatic loadDesign, so seeding off that stream left the baseline null and
// swallowed the first real user edit (no save bar). `design:loaded` also fires
// after the design has settled/normalised, so the seeded snapshot matches what a
// later saveDesign returns (no spurious dirty on open / status switch / discard).

const TemplateEditor = () => {
  const [apiError, setApiError] = useState<string>('');
  const [subjectApiError, setSubjectApiError] = useState<string>('');
  const emailEditorRef = useRef<EditorRef>(null);
  let [state, setState] = useState<any>();
  const dispatch = useDispatch();
  const orderStatusState = useSelector(orderStatusSelector);
  const templatesTab = useSelector(emailTemplatesTabSelector);
  const templateState = useSelector(emailTemplatesSelector);

  // Refs for content-dirty tracking
  const loadedDesignRef = useRef<JSONTemplate | null>(null);
  const loadedSnapshotRef = useRef<string | null>(null); // stringified baseline design for content comparison
  const isListenerBoundRef = useRef<boolean>(false);
  const [isContentDirty, setIsContentDirty] = useState<boolean>(false);

  const [getTemplateDetail, { data, isLoading: isTemplateLoading, status, isFetching }] = useLazyGetTemplateDetailQuery();
  const [updateTemplate, { isLoading }] = useUpdateTemplateMutation();

  if (isFetching) state = null;

  // Subject handlers
  const handleSubjectChange = (value: string) => {
    setSubjectApiError('');
    dispatch(
      emailSlice.actions.handleChangeEmailTemplates({
        ...templateState,
        [templatesTab]: { ...templateState[templatesTab], subject: value },
      }),
    );
  };

  // Template editor handlers
  const handleLoadTemplate = useCallback(() => {
    getTemplateDetail({ trackingStatus: orderStatusState, receiver: templatesTab }).then(({ data }) => {
      const editorInstance = emailEditorRef.current?.editor;
      if (data && data.data.parsedJsonTemplate && editorInstance) {
        const design = JSON.parse(data.data.parsedJsonTemplate);
        loadedDesignRef.current = design;
        // Bind listeners ONCE and BEFORE loadDesign, so the design:loaded event of
        // the very first load is captured.
        if (!isListenerBoundRef.current) {
          isListenerBoundRef.current = true;
          // design:loaded fires after a (programmatic) loadDesign has settled →
          // seed the baseline from the settled/normalised design.
          editorInstance.addEventListener('design:loaded', () => {
            const ed = emailEditorRef.current?.editor;
            ed?.saveDesign((current: JSONTemplate) => {
              loadedSnapshotRef.current = JSON.stringify(current);
              setIsContentDirty(false);
            });
          });
          // design:updated fires only on real user edits → compare against the
          // baseline. Ignore until the baseline has been seeded by design:loaded.
          editorInstance.addEventListener('design:updated', () => {
            const ed = emailEditorRef.current?.editor;
            if (!ed || loadedSnapshotRef.current === null) return;
            ed.saveDesign((current: JSONTemplate) => {
              // Content-based dirty: true only if the design really differs from
              // the loaded baseline (also clears on edit-then-revert).
              setIsContentDirty(JSON.stringify(current) !== loadedSnapshotRef.current);
            });
          });
        }
        loadedSnapshotRef.current = null; // re-seeded by the upcoming design:loaded
        editorInstance.loadDesign(design);
        setIsContentDirty(false);
      }
    });
  }, [getTemplateDetail, orderStatusState, templatesTab]);

  // Unified save: persists both subject and content in a single updateTemplate call
  const handleSave = () => {
    const editorInstance = emailEditorRef.current?.editor;
    if (!editorInstance) return;
    const isStatusActive = templateState[templatesTab].status.some((s) => s === orderStatusState);
    editorInstance.exportHtml(
      ({ design, html }) => {
        updateTemplate({
          status: orderStatusState,
          receiver: templatesTab,
          subject: templateState[templatesTab].subject,
          isActive: isStatusActive,
          parsedHtmlTemplate: html,
          parsedJsonTemplate: JSON.stringify(design),
        }).then((res) => {
          if ('data' in res && res.data && res.data?.state === 1) {
            setApiError('');
            setSubjectApiError('');
            loadedDesignRef.current = design;
            // Re-baseline to the just-saved content so it isn't seen as dirty.
            editorInstance.saveDesign((d: JSONTemplate) => {
              loadedSnapshotRef.current = JSON.stringify(d);
            });
            setIsContentDirty(false);
            dispatch(commonSlice.actions.handleToast(handleToastMutation(res)));
            return;
          }
          setApiError(getErrorFromAPI(res));
        });
      },
      { cleanup: true },
    );
  };

  // Unified discard: reverts subject and reloads old editor design
  const handleDiscard = () => {
    dispatch(emailSlice.actions.handleChangeEmailTemplates(state));
    const editorInstance = emailEditorRef.current?.editor;
    if (editorInstance && loadedDesignRef.current) {
      loadedSnapshotRef.current = null; // re-seeded by design:loaded after the reload
      editorInstance.loadDesign(loadedDesignRef.current);
    }
    setIsContentDirty(false);
  };

  useEffect(() => {
    handleLoadTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderStatusState, templatesTab, data]);

  useEffect(() => {
    if (status === 'fulfilled') {
      if (data) {
        const updatedStatus = data.data.isActive
          ? [...templateState[templatesTab].status, data.data?.trackingStatus]
          : templateState[templatesTab].status.filter((status) => status !== data?.data?.trackingStatus);
        dispatch(
          emailSlice.actions.handleChangeEmailTemplates({
            ...templateState,
            [templatesTab]: { ...templateState[templatesTab], status: updatedStatus, subject: data.data.subject },
          }),
        );
        setState({
          ...templateState,
          [templatesTab]: { ...templateState[templatesTab], status: updatedStatus, subject: data.data.subject },
        });
      } else {
        setState({});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, status]);

  const isSubjectDirty = state ? JSON.stringify(state) !== JSON.stringify(templateState) : false;
  const isDirty = isSubjectDirty || isContentDirty;

  const actionGroup = [
    {
      content: 'Discard',
      onAction: handleDiscard,
      primary: false,
    },
    {
      content: 'Save',
      onAction: handleSave,
      primary: true,
      loading: isLoading,
    },
  ];

  return (
    <StyledTemplateEditor>
      <ControlBar isFixed actionGroup={actionGroup} hidden={!isDirty} />

      <Card>
        <BlockStack gap="400">
          <TemplateButtons />
          {isTemplateLoading ? (
            <div style={{ height: 80 }}>
              <Text as="p">Loading...</Text>
            </div>
          ) : (
            <TextField
              id="subject"
              label="Subject"
              value={templateState[templatesTab].subject}
              onChange={handleSubjectChange}
              autoComplete="off"
              maxLength={255}
              showCharacterCount
              error={subjectApiError}
              placeholder="Enter email subject"
            />
          )}
          <BlockStack gap="200">
            <Text variant="bodyMd" as="h3">
              Content
            </Text>
            <ErrorBanner isVisible={!!apiError}>{apiError}</ErrorBanner>
            <EmailEditor ref={emailEditorRef} onLoad={handleLoadTemplate} minHeight="80vh" />
          </BlockStack>
        </BlockStack>
      </Card>
    </StyledTemplateEditor>
  );
};

export default TemplateEditor;
